import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Trophy, Briefcase, Layers, Lock } from 'lucide-react';

interface Layer {
  id: string;
  icon: React.FC<{ className?: string }>;
  title: string;
  description: string;
  color: string;
  offset: number;
}

const LAYERS: Layer[] = [
  {
    id: 'identity',
    icon: User,
    title: 'Identity Layer',
    description: 'Your unified profile connecting all Web3 wallets and Web2 accounts in one permanent, soulbound identity.',
    color: '#3BFF7A',
    offset: -30 // Adjusted slightly for a tighter, centered spread
  },
  {
    id: 'reputation',
    icon: Trophy,
    title: 'Reputation Layer',
    description: 'Cryptographically verified achievements and contributions stored permanently on Arweave—impossible to fake or manipulate.',
    color: '#0EF37F',
    offset: 50
  },
  {
    id: 'proof',
    icon: Briefcase,
    title: 'Proof of Work Layer',
    description: 'Quantifiable metrics from GitHub commits, Discord activity, X engagement, and on-chain transactions—all in one place.',
    color: '#8BFFAD',
    offset: 130
  }
];

export const PassportExplainer: React.FC = () => {
  const [isExploded, setIsExploded] = useState(false);
  const [hoveredLayer, setHoveredLayer] = useState<string | null>(null);

  return (
    <section id="ampassport" className="py-32 px-6 relative z-10">
      {/* Deep ambient glow behind the section */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-[500px] bg-green-500/5 blur-[150px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-24"
        >
          <div className="inline-flex items-center gap-3 mb-6 px-4 py-2 rounded-full bg-white/[0.03] border border-green-500/20 backdrop-blur-md shadow-[0_0_15px_rgba(0,255,157,0.05)]">
            <Layers className="w-3.5 h-3.5 text-green-400" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-medium text-green-300">
              Soulbound Architecture
            </span>
          </div>
          
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-white to-white/60 mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Your amPassport
          </h2>
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto font-light leading-relaxed">
            A non-transferable, permanent proof of your web3 identity—built on three immutable layers.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {/* Left: Interactive Glass Chip Stack */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, type: "spring", bounce: 0.2 }}
            className="flex justify-center"
          >
            <div 
              className="relative w-full max-w-[360px] h-[480px] flex items-center justify-center cursor-pointer group"
              onMouseEnter={() => setIsExploded(true)}
              onMouseLeave={() => {
                setIsExploded(false);
                setHoveredLayer(null);
              }}
              style={{ perspective: '1200px' }}
            >
              <AnimatePresence>
                {LAYERS.map((layer, index) => {
                  const Icon = layer.icon;
                  const isHovered = hoveredLayer === layer.id;
                  const explodeOffset = isExploded ? layer.offset : 0;

                  return (
                    <motion.div
                      key={layer.id}
                      className="absolute w-full h-[240px] rounded-3xl"
                      style={{ zIndex: LAYERS.length - index }}
                      animate={{
                        y: explodeOffset,
                        scale: isHovered ? 1.05 : isExploded ? 1 : 1 - (index * 0.03), // Slight scaling down for back cards when collapsed
                        rotateX: isExploded ? 0 : 8, // Flattens out slightly when collapsed
                      }}
                      transition={{ type: "spring", stiffness: 180, damping: 20 }}
                      onHoverStart={() => setHoveredLayer(layer.id)}
                      onHoverEnd={() => setHoveredLayer(null)}
                    >
                      {/* Glass Card Layer */}
                      <div 
                        className="w-full h-full rounded-[2rem] border border-white/10 backdrop-blur-xl relative overflow-hidden transition-all duration-300"
                        style={{
                          background: isHovered 
                            ? `linear-gradient(135deg, #000000cc, #0a1511cc)` 
                            : `linear-gradient(135deg, rgba(0,0,0,0.6), rgba(6,10,8,0.8))`,
                          boxShadow: isHovered 
                            ? `0 20px 40px -10px rgba(0,0,0,0.8), 0 0 30px ${layer.color}20`
                            : `0 10px 30px -10px rgba(0,0,0,0.8)`,
                        }}
                      >
                        {/* Top reflection highlight */}
                        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                        
                        {/* Inner glowing accent */}
                        <div 
                          className="absolute inset-0 opacity-20 transition-opacity duration-300"
                          style={{
                            background: `radial-gradient(120% 120% at 50% -20%, ${layer.color}40, transparent)`,
                            opacity: isHovered ? 0.4 : 0.1
                          }}
                        />

                        {/* Animated scan line */}
                        {isHovered && (
                          <motion.div
                            className="absolute inset-x-0 h-[2px] blur-[1px]"
                            style={{ backgroundColor: layer.color, opacity: 0.8 }}
                            animate={{ y: [-10, 250, -10] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
                          />
                        )}

                        {/* Content */}
                        <div className="relative z-10 p-8 h-full flex flex-col items-center justify-center text-center">
                          <div 
                            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5 shadow-lg transition-transform duration-300"
                            style={{
                              background: `${layer.color}15`,
                              border: `1px solid ${layer.color}30`,
                              color: layer.color,
                              transform: isHovered ? 'scale(1.1) translateY(-5px)' : 'scale(1)'
                            }}
                          >
                            <Icon className="w-8 h-8" />
                          </div>

                          <h3 className="text-2xl font-bold text-white tracking-wide">
                            {layer.title}
                          </h3>
                          
                          <div 
                            className="mt-3 text-[10px] uppercase tracking-[0.3em] font-mono"
                            style={{ color: `${layer.color}90` }}
                          >
                            Layer 0{index + 1}
                          </div>
                        </div>

                        {/* Bottom edge color bar */}
                        <div 
                          className="absolute bottom-0 inset-x-0 h-1.5 transition-opacity duration-300"
                          style={{
                            background: `linear-gradient(90deg, transparent, ${layer.color}, transparent)`,
                            opacity: isHovered ? 1 : 0.3
                          }}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              {/* Hover Instruction */}
              <AnimatePresence>
                {!isExploded && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
                  >
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold bg-black/40 px-4 py-2 rounded-full border border-white/5 backdrop-blur-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      Hover to explore architecture
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Right: Layer Descriptions */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-4"
          >
            {LAYERS.map((layer, index) => {
              const Icon = layer.icon;
              const isActive = hoveredLayer === layer.id || (!hoveredLayer && isExploded);

              return (
                <motion.div
                  key={`desc-${layer.id}`}
                  animate={{
                    opacity: isActive || !isExploded ? 1 : 0.3,
                    x: isActive ? 8 : 0,
                    scale: isActive ? 1.02 : 1
                  }}
                  transition={{ duration: 0.4, type: "spring" }}
                  className="p-6 rounded-2xl backdrop-blur-md border transition-all duration-300 relative overflow-hidden"
                  style={{
                    background: isActive ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.2)',
                    borderColor: isActive ? `${layer.color}40` : 'rgba(255, 255, 255, 0.05)'
                  }}
                >
                  {/* Active state subtle background glow */}
                  {isActive && (
                    <div 
                      className="absolute left-0 top-0 bottom-0 w-1" 
                      style={{ background: layer.color, boxShadow: `0 0 20px ${layer.color}` }} 
                    />
                  )}

                  <div className="flex items-start space-x-5 relative z-10">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-300"
                      style={{
                        background: isActive ? `${layer.color}20` : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${isActive ? `${layer.color}40` : 'rgba(255,255,255,0.1)'}`,
                        color: isActive ? layer.color : 'rgba(255,255,255,0.4)'
                      }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1.5">
                        <div className="text-[10px] font-mono text-white/40">0{index + 1}</div>
                        <h4 className="text-lg font-bold text-white">
                          {layer.title}
                        </h4>
                      </div>
                      <p className="text-sm leading-relaxed text-white/60 font-light">
                        {layer.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Security Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mt-8 p-5 rounded-2xl bg-[#00ff9d]/5 border border-[#00ff9d]/20 backdrop-blur-sm relative overflow-hidden group"
            >
              {/* Terminal scanline effect */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,157,0.05)_1px,transparent_1px)] bg-[size:100%_4px] pointer-events-none" />
              
              <div className="relative z-10 flex items-start space-x-4">
                <div className="mt-1 p-2 bg-[#00ff9d]/10 rounded-lg">
                  <Lock className="w-4 h-4 text-[#00ff9d]" />
                </div>
                <div className="text-sm text-green-100/80 leading-relaxed font-light">
                  <strong className="text-[#00ff9d] font-semibold tracking-wide">SOULBOUND ARCHITECTURE: </strong> 
                  Your amPassport is non-transferable and permanently tied to your identity. 
                  It cannot be sold, traded, or transferred—ensuring 100% authentic reputation.
                </div>
              </div>
            </motion.div>
            
          </motion.div>
        </div>
      </div>
    </section>
  );
};
